import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import Btn from '../ui/Btn'
import { posterUrl } from '../ui/videoSource'
import './media.css'

/*
 * MEDIA MODULE — the pieces Sponsors and Coaches' Videos share.
 *
 * The two screens are the same product: a list, a multi-language form, a video
 * picker and an upload. Everything in here is PRESENTATION ONLY — not one axios
 * call lives in this file. Each screen keeps its own requests, URLs, payloads
 * and response handling exactly as they were; they only borrow the surface.
 *
 * Nothing here defines .btn / .card / .nav-link / .form-control / .container /
 * .row / .col, and no colour is written as a literal: every value is a token.
 */

/* ------------------------------------------------------------------ *
 * DATA TABLE THEME
 * react-data-table-component takes colours as JS values, so the tokens
 * have to be read out of the cascade and handed over. The read repeats
 * whenever the theme changes — both the explicit [data-theme] switch and
 * the OS-level preference — so the grid never lags the rest of the page.
 * ------------------------------------------------------------------ */
function readTokens() {
  if (typeof window === 'undefined') return null
  const cs = window.getComputedStyle(document.documentElement)

  /* If the custom property has not landed yet (first paint, stylesheet still in
     flight) hand back the var() reference itself rather than a frozen literal:
     the browser resolves it the moment tokens.css arrives, and no colour is ever
     written as a hex/rgba literal in this file. Non-colour tokens keep a plain
     CSS keyword fallback, which is not a colour. */
  const get = (name, fallback) => {
    const value = cs.getPropertyValue(name)
    const trimmed = value ? value.trim() : ''
    return trimmed || (fallback === undefined ? `var(${name})` : fallback)
  }

  return {
    panel: get('--panel'),
    panelRaise: get('--panel-raise'),
    line: get('--line'),
    lineSoft: get('--line-soft'),
    ink: get('--ink'),
    ink2: get('--ink-2'),
    ink3: get('--ink-3'),
    accentWash: get('--accent-wash'),
    radius: get('--r-s', '6px'),
    sans: get('--sans', 'sans-serif'),
    mono: get('--mono', 'monospace'),
    step0: get('--step-0', '0.875rem'),
  }
}

export function useTableStyles() {
  const [tokens, setTokens] = useState(readTokens)

  useEffect(() => {
    const refresh = () => setTokens(readTokens())

    const observer = new MutationObserver(refresh)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class', 'style'] })

    const media = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null
    if (media) {
      if (media.addEventListener) media.addEventListener('change', refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    /* The stylesheet may still be arriving on first paint. */
    const t = window.setTimeout(refresh, 60)

    return () => {
      observer.disconnect()
      window.clearTimeout(t)
      if (media) {
        if (media.removeEventListener) media.removeEventListener('change', refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  }, [])

  return useMemo(() => {
    const t = tokens || readTokens() || {}
    return {
      table: { style: { backgroundColor: 'transparent', color: t.ink } },
      tableWrapper: { style: { display: 'table', backgroundColor: 'transparent' } },
      responsiveWrapper: { style: { backgroundColor: 'transparent' } },
      head: { style: { color: t.ink3, fontFamily: t.mono } },
      headRow: {
        style: {
          backgroundColor: 'transparent',
          borderBottomColor: t.line,
          borderBottomWidth: '1px',
          minHeight: '42px',
        },
      },
      headCells: {
        style: {
          color: t.ink3,
          fontFamily: t.mono,
          fontSize: '10px',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          fontWeight: 600,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      },
      rows: {
        style: {
          backgroundColor: 'transparent',
          color: t.ink,
          fontFamily: t.sans,
          fontSize: t.step0,
          minHeight: '54px',
          borderBottomColor: t.lineSoft,
        },
        highlightOnHoverStyle: {
          backgroundColor: t.accentWash,
          color: t.ink,
          borderBottomColor: t.lineSoft,
          outlineColor: 'transparent',
        },
        stripedStyle: { backgroundColor: t.panelRaise },
      },
      cells: { style: { paddingLeft: '10px', paddingRight: '10px', color: t.ink2 } },
      noData: { style: { backgroundColor: 'transparent', color: t.ink3, padding: '24px' } },
      progress: { style: { backgroundColor: 'transparent', color: t.ink3 } },
      pagination: {
        style: { backgroundColor: 'transparent', color: t.ink3, borderTopColor: t.line, fontFamily: t.sans },
        pageButtonsStyle: { fill: t.ink2, color: t.ink2 },
      },
    }
  }, [tokens])
}

/* ------------------------------------------------------------------ *
 * GLYPHS — inline so nothing depends on an icon font.
 * ------------------------------------------------------------------ */
export const IconPlay = (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.2v13.6L19 12 8 5.2z" /></svg>
)

export const IconPencil = (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4zM14.5 5.5l4 4" /></svg>
)

export const IconTrash = (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6" /></svg>
)

export const IconPlus = (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
)

export const IconUpload = (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V4M8 8l4-4 4 4M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" /></svg>
)

export const IconClose = (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
)

/* ------------------------------------------------------------------ *
 * NOTICE — every alert() in this module became one of these.
 * ------------------------------------------------------------------ */
export function Notice({ kind = 'info', children, onClose, closeLabel = 'Close' }) {
  if (!children) return null
  const cls = 'md-note md-note-' + (kind === 'good' || kind === 'crit' ? kind : 'info')

  return (
    <div className={cls} role={kind === 'crit' ? 'alert' : 'status'}>
      <div className="md-note-body">{children}</div>
      {onClose ? (
        <button type="button" className="md-note-x" onClick={onClose} aria-label={closeLabel}>×</button>
      ) : null}
    </div>
  )
}

/* Inline replacement for window.confirm — same question, inside the panel. */
export function Confirm({ text, confirmLabel, cancelLabel, onConfirm, onCancel, busy }) {
  return (
    <div className="md-confirm" role="alertdialog" aria-live="assertive">
      <div className="md-confirm-text">{text}</div>
      <div className="md-confirm-tools">
        <Btn onClick={onCancel} disabled={busy}>{cancelLabel}</Btn>
        <Btn className="md-danger" onClick={onConfirm} disabled={busy}>{confirmLabel}</Btn>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * FIELD — label above the control, everywhere in this module.
 * ------------------------------------------------------------------ */
export function Field({ label, htmlFor, required, hint, className, children }) {
  return (
    <div className={className ? 'md-field ' + className : 'md-field'}>
      {label ? (
        <label className="md-label" htmlFor={htmlFor}>
          {label}
          {required ? <span className="md-req" aria-hidden="true">*</span> : null}
        </label>
      ) : null}
      {children}
      {hint ? <div className="md-hint">{hint}</div> : null}
    </div>
  )
}

export function StatusPill({ on, onLabel = 'Active', offLabel = 'Inactive' }) {
  const active = String(on) === '1' || on === true
  return (
    <span className={active ? 'md-pill md-pill-on' : 'md-pill md-pill-off'}>
      <i aria-hidden="true" />
      {active ? onLabel : offLabel}
    </span>
  )
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="md-empty">
      <b>{title}</b>
      {hint ? <span>{hint}</span> : null}
      {action}
    </div>
  )
}

export function SkeletonList({ rows = 5 }) {
  const items = []
  for (let i = 0; i < rows; i += 1) items.push(i)
  return (
    <div className="md-skel-list" aria-hidden="true">
      {items.map((i) => <div key={i} className="skeleton md-skel-row" />)}
    </div>
  )
}

export function SkeletonCards({ count = 3 }) {
  const items = []
  for (let i = 0; i < count; i += 1) items.push(i)
  return (
    <div className="md-clips" aria-hidden="true">
      {items.map((i) => <div key={i} className="skeleton md-skel-card" />)}
    </div>
  )
}

export function SkeletonForm({ rows = 4 }) {
  const items = []
  for (let i = 0; i < rows; i += 1) items.push(i)
  return (
    <div className="md-skel-list" aria-hidden="true">
      {items.map((i) => (
        <div key={i}>
          <div className="skeleton md-skel-line md-skel-lead" />
          <div className="skeleton md-skel-row" />
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * SHEET — the module's dialog. Escape closes, backdrop closes,
 * the body under it stops scrolling, and under 768px it is full-bleed.
 * ------------------------------------------------------------------ */
export function Sheet({ title, subtitle, onClose, children, closeLabel = 'Close' }) {
  const ref = useRef(null)

  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key !== 'Escape') return
      /* The video dialog can open on top of a sheet. Both listen on document,
         so its stopPropagation cannot reach us — while it is up, Escape is its
         key, not ours. */
      if (document.querySelector('.vm-backdrop')) return
      ev.stopPropagation()
      onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const node = ref.current
    if (node) node.focus()
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])

  return createPortal(
    <div
      className="md-sheet-back"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="md-sheet" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <div className="md-sheet-head">
          <div>
            <div className="md-sheet-title">{title}</div>
            {subtitle ? <div className="md-sheet-sub">{subtitle}</div> : null}
          </div>
          <button type="button" className="md-sheet-x" onClick={onClose} aria-label={closeLabel}>
            {IconClose}
          </button>
        </div>
        <div className="md-sheet-body">{children}</div>
      </div>
    </div>,
    document.body
  )
}

/* ------------------------------------------------------------------ *
 * CLIPS
 * ------------------------------------------------------------------ */

/* 95 -> "1:35". The library reports seconds; a bare integer read as minutes
   was the old bug ("1:35" was printed as 1:35 but 605 printed as 10:5). */
export function formatDuration(seconds) {
  const total = Math.max(0, parseInt(seconds, 10) || 0)
  const m = Math.floor(total / 60)
  const s = total - m * 60
  return m + ':' + (s < 10 ? '0' + s : String(s))
}

/* One card, one clip — the only way video is shown in this module.
   The poster is the shared poster URL; playback is the shared VideoModal,
   opened by the caller through onPlay. */
export function ClipCard({ fileId, name, subtitle, duration, picked, onPlay, playLabel = 'Play', children }) {
  const [artBroken, setArtBroken] = useState(false)
  const poster = useMemo(() => posterUrl(fileId), [fileId])
  const showArt = !!poster && !artBroken

  return (
    <article className={picked ? 'md-clip md-clip-picked' : 'md-clip'}>
      <button type="button" className="md-clip-art" onClick={onPlay} aria-label={playLabel + (name ? ' — ' + name : '')}>
        {showArt ? (
          <img src={poster} alt="" loading="lazy" decoding="async" onError={() => setArtBroken(true)} />
        ) : null}
        <span className="md-clip-play" aria-hidden="true">{IconPlay}</span>
        {duration ? <span className="md-clip-dur">{duration}</span> : null}
      </button>

      <div className="md-clip-body">
        {name ? <div className="md-clip-name">{name}</div> : null}
        {subtitle ? <div className="md-clip-id">{subtitle}</div> : null}
        {children ? <div className="md-clip-tools">{children}</div> : null}
      </div>
    </article>
  )
}

export function ClipGrid({ single, children }) {
  return <div className={single ? 'md-clips md-clips-1' : 'md-clips'}>{children}</div>
}

/* ------------------------------------------------------------------ *
 * VIDEO LIBRARY — the picker body, identical for sponsors and coaches.
 *
 * It knows nothing about how a pick is stored: the caller passes isPicked /
 * onPick / onPreview and keeps its own request. `mode` only changes the label
 * and whether more than one clip can be on at a time.
 * ------------------------------------------------------------------ */
export function VideoLibrary({
  videos,
  loading,
  error,
  query,
  onQuery,
  isPicked,
  onPick,
  onPreview,
  busyId,
  pickLabel = 'Select',
  pickedLabel = 'Selected',
  emptyTitle = 'No videos',
  emptyHint = 'No videos match these filters.',
}) {
  const list = Array.isArray(videos) ? videos.filter((item) => item && item.id) : []

  return (
    <>
      <div className="md-search">
        <input
          className="md-input"
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search videos..."
          aria-label="Search videos"
        />
        {!loading ? <span className="md-count">{list.length} videos</span> : null}
      </div>

      {error ? <Notice kind="crit">{error}</Notice> : null}

      {loading ? <SkeletonCards count={6} /> : null}

      {!loading && list.length === 0 ? (
        <EmptyState title={emptyTitle} hint={emptyHint} />
      ) : null}

      {!loading && list.length > 0 ? (
        <ClipGrid>
          {list.map((item) => {
            const on = typeof isPicked === 'function' ? isPicked(item) : false
            const busy = busyId === item.id

            return (
              <ClipCard
                key={item.id}
                fileId={item.id}
                name={item.friendlyName ? item.friendlyName : item.id}
                subtitle={item.friendlyName ? item.id : ''}
                duration={item.duration ? formatDuration(item.duration) : ''}
                picked={on}
                onPlay={() => onPreview(item)}
              >
                <Btn
                  variant={on ? 'default' : 'accent'}
                  onClick={() => onPick(item, !on)}
                  disabled={busy}
                  aria-pressed={on}
                >
                  {busy ? '…' : on ? pickedLabel : pickLabel}
                </Btn>
              </ClipCard>
            )
          })}
        </ClipGrid>
      ) : null}
    </>
  )
}

/* ------------------------------------------------------------------ *
 * MEDIA UPLOAD — the file control with real progress and inline errors.
 *
 * Presentation only: the caller owns the request, the progress number and
 * the messages. It gets a labelled drop zone (click, keyboard and drag all
 * work), a token progress bar, the current image and a delete affordance.
 * ------------------------------------------------------------------ */
export function MediaUpload({
  inputId,
  inputRef,
  hint,
  fileName,
  onFile,
  onSubmit,
  uploading,
  percent,
  canSubmit,
  submitLabel,
  notice,
  preview,
  previewAlt,
  hasFile,
  onDelete,
  deleteLabel = 'Delete',
  emptyPreview = 'No image yet',
}) {
  const [over, setOver] = useState(false)

  const take = useCallback((files) => {
    if (!files || !files.length) return
    onFile(files[0])
  }, [onFile])

  return (
    <div className="md-upload">
      <div className="md-upload-main">
        <form onSubmit={onSubmit} className="md-form">
          <label
            className={over ? 'md-drop md-drop-over' : 'md-drop'}
            htmlFor={inputId}
            onDragOver={(e) => { e.preventDefault(); setOver(true) }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files) }}
          >
            <span className="md-drop-mark" aria-hidden="true">{IconUpload}</span>
            <span className="md-drop-text">
              <span className="md-drop-name">{fileName}</span>
              {hint ? <span className="md-drop-hint">{hint}</span> : null}
            </span>
            <input
              id={inputId}
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={(e) => take(e.target.files)}
            />
          </label>

          {uploading ? (
            <div className="md-progress-row">
              <div className="md-progress-head">
                <span>Uploading</span>
                <b>{percent}%</b>
              </div>
              <div
                className="md-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
              >
                <i style={{ width: percent + '%' }} />
              </div>
            </div>
          ) : null}

          {notice}

          <div className="md-actions">
            <Btn variant="accent" type="submit" disabled={!canSubmit || uploading}>
              {uploading ? 'Uploading…' : submitLabel}
            </Btn>
          </div>
        </form>
      </div>

      <div className="md-preview">
        <div className="md-preview-frame">
          {hasFile && preview ? (
            <img src={preview} alt={previewAlt || ''} />
          ) : (
            <span className="md-preview-empty">{emptyPreview}</span>
          )}
        </div>
        {hasFile && onDelete ? (
          <Btn className="md-danger" onClick={onDelete}>{deleteLabel}</Btn>
        ) : null}
      </div>
    </div>
  )
}

export default VideoLibrary
